import {Injectable} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {AsaxElasticService, buildQueryRange, buildQueryTermEqual, ElasticFiltersService} from 'asax-elastic';
import {catchError, map, tap} from 'rxjs/operators';
import {Extremes} from 'asax-common';
import {
  ChartFilter
} from './frequency-view-chart-filters/frequency-view-chart-filters-container/frequency-view-chart-filters-container.component';

export interface Splat {
  readonly id?: string;
  readonly speciesId?: number;
  readonly description?: string;
  readonly name?: string;
  readonly frequency?: number;
  readonly redshiftFrequency?: number;
  readonly transition?: string;
  readonly intensity?: number;
  readonly labels?: string[];
  readonly lineFilter?: string;
  readonly order?: number;
}

const splatFiltersMappingSearchProperties = {molecules: {property: 'labels'}, lines: {property: 'lineFilter'}};

@Injectable({
  providedIn: 'root'
})
export class SplatsService {
  private splatsCache = new Map<string, Splat>();

  private splatHovered = new Subject<Splat>();


  constructor(private readonly asaxElasticService: AsaxElasticService,
              private readonly elasticFiltersService: ElasticFiltersService) {
  }

  getSplatHovered(): Observable<Splat> {
    return this.splatHovered.asObservable();
  }

  setSplatHovered(splat: Splat): void {
    this.splatHovered.next(splat);
  }

  fetchSplats(chartWidth: number, extremes: Extremes, filters: ChartFilter[]): Observable<Splat[]> {
    const redshiftValue = filters.find(filter => filter.id === 'redshift')?.value || 0;
    const redshiftFrom = extremes.from * (1 + redshiftValue);
    const redshiftTo = extremes.to * (1 + redshiftValue);

    const splatsQuery = this.elasticFiltersService.buildElasticDSLQueryBodyFromFilters(filters, splatFiltersMappingSearchProperties);
    const splatsAggregation = buildSplatsAggregation(chartWidth, redshiftFrom, redshiftTo);

    return this.asaxElasticService.searchByQuery({
      index: 'splats',
      body: {
        _source: ['id', 'frequency', 'description', 'transition'],
        from: 0,
        size: 0,
        query: splatsQuery,
        aggregations: splatsAggregation
      }
    }).pipe(
      map((response: any) => mapResponseToSplats(response)),
      map((splats: Splat[]) => addRedshiftFrequencyToSplats(splats, redshiftValue)),
      catchError(() => of([]))
    );
  }

  fetchSplat(id: string): Observable<Splat> {
    const existingSplat = this.splatsCache.get(id);

    if (existingSplat) {
      return of({...existingSplat});
    }

    const query = buildQueryTermEqual('_id', id);
    return this.asaxElasticService.searchByQuery({
      index: 'splats', body: {
        _source: ['description', 'name', 'transition', 'frequency'],
        from: 0,
        size: 1,
        query
      }
    }).pipe(
      map((response: any) => mapResponseToSplat(response)),
      tap((splat) => this.splatsCache.set(splat.id, splat)),
      catchError(() => of(undefined))
    );
  }
}


const mapResponseToSplat = (response: any): Splat => {
  if (response?.hits?.hits) {
    return response.hits
      .hits
      .map(({_source}) => _source)[0];
  }

  return undefined;
};

const mapResponseToSplats = (response: any): Splat[] => {
  const splats: Splat[] = [];

  if (response?.aggregations) {
    for (const aggregation in response?.aggregations) {
      if (response?.aggregations.hasOwnProperty(aggregation)) {
        const bucket = response?.aggregations[aggregation];
        if (bucket?.doc_count > 0) {
          bucket?.top_hits?.hits?.hits?.map(({_source}) => _source)
            .forEach(splat => splats.push(splat));
        }
      }
    }
  }

  return splats;
};

const addRedshiftFrequencyToSplats = (splats: Splat[], redshiftValue: number) => {
  const redshiftFactor = 1 / (1 + redshiftValue);
  return splats?.map(splat => ({...splat, redshiftFrequency: splat.frequency * redshiftFactor}));
}

const buildSplatsAggregation = (chartWidth: number, from: number, to: number) => {
  const numberOfIntervals = calculateNumberIntervalsWhereToFetchSplats(chartWidth);
  const intervalsWhereToFetchSplats = buildIntervalsWhereToFetchSplats(from, to, numberOfIntervals);

  const aggregations = {};
  intervalsWhereToFetchSplats.forEach(interval =>
    aggregations[`${interval.from}-${interval.to}`] = {
      filter: buildQueryRange('frequency', interval.from, interval.to),
      aggs: {top_hits: {top_hits: {size: 1, sort: [{order: {order: 'desc'}}]}}}
    }
  );

  return aggregations;
};

const calculateNumberIntervalsWhereToFetchSplats = (chartWidth: number): number => {
  const numberPixelsBetweenBands = 30;
  return Math.floor(chartWidth / numberPixelsBetweenBands);
};

const buildIntervalsWhereToFetchSplats = (from: number, to: number, numberOfIntervals: number): any[] => {
  const sizeOfEachInterval = (to - from) / numberOfIntervals;
  const sequenceOfNumberOfIntervals = [...Array(numberOfIntervals).keys()];
  return sequenceOfNumberOfIntervals.map((element, index) => ({
    from: from + (index * sizeOfEachInterval),
    to: from + (index * sizeOfEachInterval) + sizeOfEachInterval
  }));
};
